/**
 * The sections a client is offered on the booking page.
 *
 *   "if they hit IV drip, all they see is IV drips and not IM"
 *   "When you seperate into the sections be sure to add the IV drips in the
 *    specialized area to show up with the drip section / There is only 4"
 *
 * Her IV family is sixteen drips, eleven injections, nine pushes and six NAD
 * infusions in one scroll. The sections come from her own categories, because
 * she is the one who renames and reorders them; two categories that should
 * read as one section say so by sharing a booking_group.
 *
 * Here rather than in the page so it can be tested on her real menu.
 */
export interface SectionCategory {
  id: string;
  name: string;
  color: string | null;
  position: number;
  booking_group?: string | null;
}

export interface SectionService {
  id: string;
  category_id: string | null;
}

export interface BookingSection<S extends SectionService> {
  id: string;
  name: string;
  color: string | null;
  services: S[];
}

export function bookingSections<S extends SectionService>(
  categories: readonly SectionCategory[],
  services: readonly S[],
): BookingSection<S>[] {
  const cats = [...categories].sort((a, b) => a.position - b.position);
  const byCat = new Map<string, S[]>();
  for (const s of services) {
    const key = s.category_id ?? "__uncategorized__";
    const list = byCat.get(key) ?? [];
    list.push(s);
    byCat.set(key, list);
  }
  const out: BookingSection<S>[] = [];
  for (const c of cats) {
    const own = byCat.get(c.id) ?? [];
    byCat.delete(c.id);
    if (own.length === 0) continue; // an empty category is not a section
    const label = c.booking_group?.trim() || c.name;
    // Her first category in a group sets the name, the order and the colour.
    const existing = out.find((g) => g.name === label);
    if (existing) existing.services.push(...own);
    else out.push({ id: c.id, name: label, color: c.color, services: own });
  }
  // Services whose category is missing from the menu still deserve a home.
  const leftovers = [...byCat.values()].flat();
  if (leftovers.length > 0) out.push({ id: "__other__", name: "More treatments", color: null, services: leftovers });
  return out;
}
